/**
 * Stream Settings Store
 *
 * Comprehensive, persisted settings store for the Stream operator portal.
 * Modeled after Loft's settingsStore pattern: Zustand + persist middleware
 * with localStorage, typed accessors, and transient state partializing.
 *
 * Settings are organised into sections that map to the settings modal nav:
 *   - General: theme, UI scale, font family, sidebar behaviour
 *   - Data Display: table density, default page sizes, timestamp format
 *   - Notifications: master toggle, volume, event types
 *   - Developer: dev logs, debug panel, API inspector
 *   - About: app version, environment info (read-only)
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

// ── Type definitions ──────────────────────────────────────────────────────────

export type ThemePreference = 'light' | 'dark' | 'system'
export type UIScale = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type FontFamily = 'inter' | 'system' | 'geist' | 'jetbrains-mono' | 'ibm-plex' | 'source-sans'
export type CodeFontFamily = 'jetbrains-mono' | 'fira-code' | 'source-code' | 'ibm-plex-mono' | 'system-mono' | 'cascadia'
export type TimestampFormat = 'relative' | 'absolute' | 'iso'
export type TableDensity = 'compact' | 'default' | 'comfortable'
export type DateFormat = 'us' | 'eu' | 'iso'
export type NumberFormat = 'us' | 'eu' | 'indian'
export type SidebarPosition = 'left' | 'right'
export type DefaultPageSize = 25 | 50 | 100 | 250

/**
 * Where Stream lands the user after the splash → operator selector flow.
 *
 *  - 'kodeon-splash'  Empty AppShell with the brand "Kodeon" spelled out in
 *                     particles in the main content area. The user picks any
 *                     sidebar item to navigate from there.
 *
 *  - { route: '/x' }  Land directly on the named route. The route must be one
 *                     of the user-facing routes from `routes.tsx` (events,
 *                     analytics, queue, etc.).
 */
export type LandingPagePreference =
  | { kind: 'kodeon-splash' }
  | { kind: 'route'; route: string }

export const DEFAULT_LANDING_PAGE: LandingPagePreference = {
  kind: 'kodeon-splash',
}

/**
 * Catalogue of routes the user can pick from in Settings → Startup. Mirrors
 * the user-facing entries in `routes.tsx`. Detail / sub routes (e.g.
 * `/customers/:id`) are intentionally omitted — those wouldn't make sense as
 * a landing page.
 */
export interface LandingPageOption {
  route: string
  label: string
  group: string
}

export const LANDING_PAGE_OPTIONS: LandingPageOption[] = [
  // Observe
  { route: '/events', label: 'Events', group: 'Observe' },
  // Operate
  { route: '/queue', label: 'Queue', group: 'Operate' },
  { route: '/automations', label: 'Automations', group: 'Operate' },
  { route: '/deferred', label: 'Deferred Items', group: 'Operate' },
  // Agents
  { route: '/agents', label: 'Agents', group: 'Agents' },
  { route: '/sessions', label: 'Sessions', group: 'Agents' },
  { route: '/memory', label: 'Memory', group: 'Agents' },
  { route: '/artifacts', label: 'Artifacts', group: 'Agents' },
  // Customers
  { route: '/customers', label: 'Customers', group: 'Customers' },
  // Commerce
  { route: '/purchases', label: 'Purchases', group: 'Commerce' },
  { route: '/signups', label: 'Sign Ups', group: 'Commerce' },
  { route: '/catalog', label: 'Catalog', group: 'Commerce' },
  // Communications
  { route: '/inbox', label: 'Inbox', group: 'Communications' },
  { route: '/email-templates', label: 'Email Templates', group: 'Communications' },
  // Platform Health
  { route: '/health', label: 'Health', group: 'Platform' },
]

// ── Notification preferences ──────────────────────────────────────────────────

export interface NotificationPreferences {
  /** Master on/off for all notifications. */
  enabled: boolean
  /** Volume 0–100 for notification sounds. */
  volume: number
  /** Show desktop notifications for critical alerts. */
  criticalAlerts: boolean
  /** Show notifications for agent errors. */
  agentErrors: boolean
  /** Show notifications for queue items needing attention. */
  queueAlerts: boolean
  /** Show notifications for new customer signups. */
  newCustomers: boolean
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPreferences = {
  enabled: false,
  volume: 60,
  criticalAlerts: true,
  agentErrors: true,
  queueAlerts: false,
  newCustomers: false,
}

// ── Data display preferences ──────────────────────────────────────────────────

export interface DataDisplayPreferences {
  /** Table row density. */
  tableDensity: TableDensity
  /** Default page size for paginated tables. */
  defaultPageSize: DefaultPageSize
  /** Timestamp display format across the app. */
  timestampFormat: TimestampFormat
  /** Date format preference. */
  dateFormat: DateFormat
  /** Number formatting (thousands separator). */
  numberFormat: NumberFormat
  /** Whether to show event payload previews in the stream. */
  showEventPayloads: boolean
  /** Whether to wrap long text in table cells. */
  wrapTableText: boolean
  /** Whether to show row numbers in tables. */
  showRowNumbers: boolean
  /** Whether to highlight new items with animation. */
  highlightNewItems: boolean
}

export const DEFAULT_DATA_DISPLAY: DataDisplayPreferences = {
  tableDensity: 'default',
  defaultPageSize: 50,
  timestampFormat: 'relative',
  dateFormat: 'us',
  numberFormat: 'us',
  showEventPayloads: true,
  wrapTableText: false,
  showRowNumbers: false,
  highlightNewItems: true,
}

// ── Developer preferences ─────────────────────────────────────────────────────

export interface DeveloperPreferences {
  /** Show developer log panel. */
  showDevLogs: boolean
  /** Show API request inspector in the status bar. */
  showApiInspector: boolean
  /** Log API requests to the console. */
  logApiRequests: boolean
  /** Show performance metrics overlay. */
  showPerfMetrics: boolean
  /** Enable experimental features. */
  experimentalFeatures: boolean
}

export const DEFAULT_DEVELOPER_PREFS: DeveloperPreferences = {
  showDevLogs: false,
  showApiInspector: false,
  logApiRequests: false,
  showPerfMetrics: false,
  experimentalFeatures: false,
}

// ── Auto-refresh preferences ──────────────────────────────────────────────────

export interface AutoRefreshPreferences {
  /** Whether auto-refresh is enabled globally. */
  enabled: boolean
  /** Interval in seconds for auto-refreshing data views. */
  intervalSeconds: number
  /** Pause auto-refresh when the window is not focused. */
  pauseWhenHidden: boolean
}

export const DEFAULT_AUTO_REFRESH: AutoRefreshPreferences = {
  enabled: true,
  intervalSeconds: 30,
  pauseWhenHidden: true,
}

// ── Store interface ───────────────────────────────────────────────────────────

interface SettingsState {
  // ── General ──
  uiScale: UIScale
  fontFamily: FontFamily
  codeFontFamily: CodeFontFamily
  sidebarPosition: SidebarPosition
  /** Whether to animate page transitions. */
  animateTransitions: boolean
  /** Whether to show the personal stats bar in the header. */
  showPersonalStats: boolean
  /** Whether to show breadcrumbs in the content area. */
  showBreadcrumbs: boolean
  /** Whether to reduce motion for accessibility. */
  reduceMotion: boolean

  // ── Startup ──
  /** Where to land the user after sign-in / on app launch. */
  landingPage: LandingPagePreference

  // ── Data Display ──
  dataDisplay: DataDisplayPreferences

  // ── Notifications ──
  notifications: NotificationPreferences

  // ── Developer ──
  developer: DeveloperPreferences

  // ── Auto-refresh ──
  autoRefresh: AutoRefreshPreferences

  // ── Transient state (not persisted) ──

  /** Whether the settings modal is currently open. */
  settingsOpen: boolean
  /** Which section of the settings modal is active. */
  activeSettingsSection: SettingsSection

  // ── Actions ──

  // General
  setUIScale: (scale: UIScale) => void
  setFontFamily: (family: FontFamily) => void
  setCodeFontFamily: (family: CodeFontFamily) => void
  setSidebarPosition: (position: SidebarPosition) => void
  setAnimateTransitions: (animate: boolean) => void
  setShowPersonalStats: (show: boolean) => void
  setShowBreadcrumbs: (show: boolean) => void
  setReduceMotion: (reduce: boolean) => void

  // Startup
  setLandingPage: (pref: LandingPagePreference) => void

  // Data Display
  setDataDisplay: <K extends keyof DataDisplayPreferences>(key: K, value: DataDisplayPreferences[K]) => void

  // Notifications
  setNotification: <K extends keyof NotificationPreferences>(key: K, value: NotificationPreferences[K]) => void

  // Developer
  setDeveloper: <K extends keyof DeveloperPreferences>(key: K, value: DeveloperPreferences[K]) => void

  // Auto-refresh
  setAutoRefresh: <K extends keyof AutoRefreshPreferences>(key: K, value: AutoRefreshPreferences[K]) => void

  // Settings modal
  openSettings: (section?: SettingsSection) => void
  closeSettings: () => void
  setActiveSettingsSection: (section: SettingsSection) => void
}

// ── Settings sections ─────────────────────────────────────────────────────────

export type SettingsSection =
  | 'general'
  | 'startup'
  | 'data-display'
  | 'notifications'
  | 'shortcuts'
  | 'environment'
  | 'developer'
  | 'updates'
  | 'about'

// ── Store creation ────────────────────────────────────────────────────────────

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      // ── General defaults ──
      uiScale: 'md',
      fontFamily: 'inter',
      codeFontFamily: 'jetbrains-mono',
      sidebarPosition: 'left',
      animateTransitions: true,
      showPersonalStats: true,
      showBreadcrumbs: true,
      reduceMotion: false,

      // ── Startup defaults ──
      landingPage: { ...DEFAULT_LANDING_PAGE },

      // ── Data Display defaults ──
      dataDisplay: { ...DEFAULT_DATA_DISPLAY },

      // ── Notifications defaults ──
      notifications: { ...DEFAULT_NOTIFICATION_PREFS },

      // ── Developer defaults ──
      developer: { ...DEFAULT_DEVELOPER_PREFS },

      // ── Auto-refresh defaults ──
      autoRefresh: { ...DEFAULT_AUTO_REFRESH },

      // ── Transient state ──
      settingsOpen: false,
      activeSettingsSection: 'general',

      // ── Actions ──

      setUIScale: (uiScale) => set({ uiScale }),
      setFontFamily: (fontFamily) => set({ fontFamily }),
      setCodeFontFamily: (codeFontFamily) => set({ codeFontFamily }),
      setSidebarPosition: (sidebarPosition) => set({ sidebarPosition }),
      setAnimateTransitions: (animateTransitions) => set({ animateTransitions }),
      setShowPersonalStats: (showPersonalStats) => set({ showPersonalStats }),
      setShowBreadcrumbs: (showBreadcrumbs) => set({ showBreadcrumbs }),
      setReduceMotion: (reduceMotion) => set({ reduceMotion }),

      setLandingPage: (landingPage) => set({ landingPage }),

      setDataDisplay: (key, value) =>
        set((state) => ({
          dataDisplay: { ...state.dataDisplay, [key]: value },
        })),

      setNotification: (key, value) =>
        set((state) => ({
          notifications: { ...state.notifications, [key]: value },
        })),

      setDeveloper: (key, value) =>
        set((state) => ({
          developer: { ...state.developer, [key]: value },
        })),

      setAutoRefresh: (key, value) =>
        set((state) => ({
          autoRefresh: { ...state.autoRefresh, [key]: value },
        })),

      openSettings: (section) =>
        set({
          settingsOpen: true,
          ...(section ? { activeSettingsSection: section } : {}),
        }),

      closeSettings: () => set({ settingsOpen: false }),

      setActiveSettingsSection: (activeSettingsSection) => set({ activeSettingsSection }),
    }),
    {
      name: 'stream-settings',
      // v2 drops the legacy global `pageLocksOverride` field. Migration
      // strips it from any user's existing localStorage so the new per-page,
      // per-mount lock behaviour takes effect immediately — without it,
      // a previously-stored `{ value: false }` would still rehydrate as an
      // untracked field and could leak into future readers.
      version: 2,
      storage: createJSONStorage(() => localStorage),
      migrate: (persistedState, version) => {
        if (!persistedState || typeof persistedState !== 'object') return persistedState
        if (version < 2) {
          // Strip the legacy field. Cast through unknown because the
          // pre-migration shape is no longer represented in the type.
          const { pageLocksOverride: _legacy, ...rest } = persistedState as Record<string, unknown>
          return rest as unknown as SettingsState
        }
        return persistedState as SettingsState
      },
      // Exclude transient state from persistence
      partialize: (state) => {
        const {
          settingsOpen: _so,
          activeSettingsSection: _ass,
          // Exclude all action functions (they are re-created on store init)
          setUIScale: _1, setFontFamily: _2, setCodeFontFamily: _3,
          setSidebarPosition: _4, setAnimateTransitions: _5,
          setShowPersonalStats: _6, setShowBreadcrumbs: _7, setReduceMotion: _8,
          setDataDisplay: _9, setNotification: _10, setDeveloper: _11,
          setAutoRefresh: _12, openSettings: _13, closeSettings: _14,
          setActiveSettingsSection: _15, setLandingPage: _16,
          ...persisted
        } = state
        return persisted as typeof state
      },
    }
  )
)
